import { useMemo, useState } from 'react'
import { SpeakButton } from '@web/lib/audio'
import type { ExerciseRendererProps } from './types'

/**
 * Tap a Portuguese word, then tap its English match. Two taps, no dragging,
 * and it works identically with a screen reader.
 */
export function MatchingExercise({
  content,
  response,
  onChange,
  locked,
  result,
}: ExerciseRendererProps<'matching'>) {
  const [activeLeft, setActiveLeft] = useState<number | null>(null)
  const pairs = response?.pairs ?? []

  // The right column is shuffled; without this the answer is just "match the
  // rows in order", which tests nothing.
  const rightOrder = useMemo(() => {
    const indices = content.pairs.map((_, i) => i)
    let seed = content.pairs.length * 104729 + content.pairs[0]!.left.length
    for (let i = indices.length - 1; i > 0; i--) {
      seed = (seed * 1103515245 + 12345) % 2147483648
      const j = seed % (i + 1)
      ;[indices[i], indices[j]] = [indices[j] as number, indices[i] as number]
    }
    return indices
  }, [content.pairs])

  const rightFor = (leftIndex: number) =>
    pairs.find((p) => p.leftIndex === leftIndex)?.rightIndex ?? null

  function choose(rightIndex: number) {
    if (activeLeft === null) return
    const next = pairs.filter((p) => p.leftIndex !== activeLeft && p.rightIndex !== rightIndex)
    next.push({ leftIndex: activeLeft, rightIndex })
    onChange({ type: 'matching', pairs: next })
    setActiveLeft(null)
  }

  return (
    <div className="space-y-3">
      {!locked && (
        <p aria-live="polite" className="text-sm text-ink-500">
          {activeLeft === null
            ? 'Tap a Portuguese word, then tap its match.'
            : `Now tap the match for “${content.pairs[activeLeft]?.left}”.`}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
      <ul className="space-y-2">
        {content.pairs.map((pair, leftIndex) => {
          const matched = rightFor(leftIndex)
          const isActive = activeLeft === leftIndex
          const verdict = locked ? result?.feedback.parts?.[leftIndex] : undefined

          let tone = 'ring-sand-200 bg-white'
          if (verdict === 'correct') tone = 'ring-brand-500 bg-brand-50'
          else if (verdict === 'wrong') tone = 'ring-clay-300 bg-clay-50'
          else if (isActive) tone = 'ring-brand-600 ring-2 bg-brand-50'
          else if (matched !== null) tone = 'ring-brand-300 bg-brand-50/50'

          return (
            <li key={pair.left} className={`flex items-center gap-1.5 rounded-xl ring-1 ${tone}`}>
              <button
                type="button"
                disabled={locked}
                aria-pressed={isActive}
                onClick={() => setActiveLeft(isActive ? null : leftIndex)}
                className="min-h-13 flex-1 rounded-xl px-3 py-2 text-left"
                lang="pt-BR"
              >
                <span className="block">{pair.left}</span>
                {matched !== null && (
                  <span className="mt-0.5 block text-xs text-ink-500">
                    → {content.pairs[matched]?.right}
                  </span>
                )}
              </button>
              {/* She cannot reasonably match words she has never heard said. */}
              <SpeakButton text={pair.left} size="sm" className="mr-1.5" />
            </li>
          )
        })}
      </ul>

      <ul className="space-y-2">
        {rightOrder.map((rightIndex) => {
          const taken = pairs.some((p) => p.rightIndex === rightIndex)
          return (
            <li key={content.pairs[rightIndex]?.right}>
              <button
                type="button"
                disabled={locked || activeLeft === null}
                onClick={() => choose(rightIndex)}
                // Dimmed only once an answer is locked in. Before that the
                // column stays visually available even though a left item must
                // be picked first — greying it out read as "broken".
                className={`min-h-13 w-full rounded-xl px-3 py-2 text-left ring-1 ring-sand-200 ${
                  taken ? 'bg-sand-100 text-ink-500' : 'bg-white'
                } ${locked ? 'opacity-60' : ''}`}
              >
                {content.pairs[rightIndex]?.right}
              </button>
            </li>
          )
        })}
        </ul>
      </div>
    </div>
  )
}
